import { Globe, ArrowSquareOut } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Fase 9.3.1 — Botón "Abrir KimKim" del panel derecho.
 *
 * Reglas de visibilidad (aprobadas por Eduardo):
 *   - Trip con `external_refs.kimkim_trip_slug_original_case`:
 *     visible, funcional. URL: /t/<original_case>.
 *   - Trip con `external_refs.kimkim_trip_slug` (lowercase) pero SIN
 *     original_case: visible, tooltip warning "URL puede fallar
 *     (casing no confirmado)". Usa el lowercase igual.
 *   - Trip no-KimKim: componente devuelve null (oculto).
 *
 * Se renderiza en `InboxPage.jsx` en la parte alta del aside derecho,
 * arriba de `<SofiPanel>`. Mismo estilo que "Abrir en SOFI" (mismo
 * tamaño, misma prominencia) pero con paleta indigo/violet para
 * distinguirlos a golpe de vista.
 */
export default function KimKimTripLink({ trip }) {
    const externalRefs = trip?.external_refs || {};
    const slugOriginal = externalRefs.kimkim_trip_slug_original_case;
    const slugLc = externalRefs.kimkim_trip_slug;

    // Trip no-KimKim → oculto.
    if (!slugOriginal && !slugLc) return null;

    const slugToUse = slugOriginal || slugLc;
    const hasCasingConfirmed = Boolean(slugOriginal);
    const url = `https://www.kimkim.com/t/${slugToUse}`;

    const button = (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md
                       bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold
                       transition-colors focus-visible:outline focus-visible:outline-2
                       focus-visible:outline-indigo-400"
            data-testid="kimkim-open-trip-btn"
            data-slug-used={slugToUse}
            data-casing-confirmed={hasCasingConfirmed ? "yes" : "no"}
        >
            <Globe size={13} weight="bold" />
            Abrir en KimKim
            <ArrowSquareOut size={12} weight="bold" className="opacity-80" />
        </a>
    );

    if (hasCasingConfirmed) {
        return (
            <div className="flex items-center gap-2" data-testid="kimkim-trip-link">
                {button}
                <span className="text-[10px] text-slate-400 font-mono select-all">
                    {slugToUse}
                </span>
            </div>
        );
    }

    return (
        <TooltipProvider delayDuration={150}>
            <div className="flex items-center gap-2" data-testid="kimkim-trip-link">
                <Tooltip>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                        URL puede fallar (casing no confirmado). Slug lowercase se
                        intenta contra KimKim; si devuelve 404 pedile a un admin
                        que confirme la URL original.
                    </TooltipContent>
                </Tooltip>
                <span className="text-[10px] text-amber-600 font-mono select-all">
                    {slugToUse}?
                </span>
            </div>
        </TooltipProvider>
    );
}
